import React from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useT } from "@/lib/LanguageContext";
import useClientBookingPolicy from "./useClientBookingPolicy";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";

const MIN_NOTICE_MIN = 0;
// booking_policy.ClientBookingPolicy.min_notice_hours ceiling (Field(ge=0, le=...)) —
// effectively unbounded for a practice's real use, but still a real validated ceiling
// on the backend, so the input carries the same one rather than an unbounded `max`.
const MIN_NOTICE_MAX = 1_000_000;

// Mirrors the backend's own Field(ge=0, le=MIN_NOTICE_MAX): a typed "-5" or "9999999"
// currently only fails after the round trip (settings.clientBooking.invalidNotice,
// caught in useClientBookingPolicy's 422 branch). Reusing that same key here stops the
// round trip instead of adding a second message for the identical fact.
const POLICY_SCHEMA = z.object({ min_notice_hours: z.union([z.string(), z.number()]) })
  .superRefine((val, ctx) => {
    const n = Number(val.min_notice_hours);
    if (!Number.isFinite(n) || n < MIN_NOTICE_MIN || n > MIN_NOTICE_MAX) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["min_notice_hours"], message: "settings.clientBooking.invalidNotice" });
    }
  });

/**
 * Client self-service card rendered inside SettingsAdmin, next to
 * NotificationSettingsCard — same shape and data-testid convention.
 */
export default function ClientBookingPolicyCard() {
  const t = useT("admin");
  const tc = useT("common");
  const { policy, setField, save, busy, error, saved } = useClientBookingPolicy();
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const handleSave = () => {
    if (validate(POLICY_SCHEMA, { min_notice_hours: policy.min_notice_hours })) {
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    save();
  };

  return (
    <div
      data-testid="booking-policy-card"
      className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-7 space-y-5 max-w-3xl"
    >
      <div>
        <h2 className="font-display text-2xl text-eh-ink">{t("settings.clientBooking.title")}</h2>
        <p className="text-eh-muted text-sm mt-1">{t("settings.clientBooking.subtitle")}</p>
      </div>

      {error && (
        <p data-testid="booking-policy-error" className="text-sm text-red-700">
          {error}
        </p>
      )}

      {!policy ? (
        !error && <Loader2 className="animate-spin text-eh-muted" size={18} />
      ) : (
        <>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="booking-policy-master-toggle"
              checked={!!policy.enabled}
              onChange={(e) => setField("enabled", e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-eh-sage-deep"
            />
            <span className="text-sm text-eh-ink">{t("settings.clientBooking.master")}</span>
          </label>

          <div className="pl-7">
            <label className="text-xs uppercase tracking-[0.18em] text-eh-muted block mb-1">
              {t("settings.clientBooking.minNotice")}
            </label>
            <input
              type="number"
              min={MIN_NOTICE_MIN}
              max={MIN_NOTICE_MAX}
              data-testid="booking-policy-min-notice-input"
              value={policy.min_notice_hours}
              disabled={!policy.enabled}
              onChange={(e) => { setField("min_notice_hours", e.target.value); clearFieldError("min_notice_hours"); }}
              className={`w-32 bg-white border rounded-xl px-3 py-2 text-sm text-eh-ink outline-none focus:border-eh-sage-deep disabled:opacity-50 ${fieldErrors.min_notice_hours ? invalidInputClass : "border-eh-border-soft"}`}
            />
            <FieldError
              message={fieldErrors.min_notice_hours ? t(fieldErrors.min_notice_hours) : ""}
              testId="booking-policy-min-notice-input-error"
            />
            <p className="text-xs text-eh-muted mt-1">{t("settings.clientBooking.minNoticeHint")}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="booking-policy-save"
              onClick={handleSave}
              disabled={busy}
              className="btn-primary text-sm"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}{" "}
              {t("settings.clientBooking.save")}
            </button>
            {saved && <span className="text-sm text-eh-sage-deep">✓ {t("settings.clientBooking.saved")}</span>}
          </div>
        </>
      )}
    </div>
  );
}
